import { ref, shallowRef } from 'vue'
import * as THREE from 'three'
import { ARTICLE_COLOR_PALETTE, articleColorIndexFor } from '@/utils/articleColor'
import { setMeshAppearance } from '@/utils/meshAppearance'
import {
  HintIndex,
  type MeshHint,
  type SelectableHint,
  bestMatchingHint,
  buildGlbNameMapping,
  distinctAncestorNames,
  matchesHintsWithMapping,
  nameMatchesArticle,
} from '@/utils/glbMeshMatching'

export type { MeshHint, SelectableHint }

export const DIM_OPACITY = 0.15

type Viewport = {
  cameraX: number
  cameraY: number
  cameraZ: number
  targetX: number
  targetY: number
  targetZ: number
}

type ApplyOpts = {
  applyViewport?: (v: Viewport) => void
  refitToBox?: (box: THREE.Box3) => void
  savedViewport?: Viewport | null
  skipRecenter?: boolean
  knownArticleNames?: Set<string>
  colorMap?: Map<string, number>
  meshesOfInstances?: (hint: MeshHint, indexes: number[]) => Set<THREE.Mesh>
  hiddenArticles?: Set<string>
}

export function useMeshHighlighting(
  allMeshes: THREE.Mesh[],
  meshAncestorNames: Map<THREE.Mesh, string[]>,
) {
  const highlightedPartCount = ref(0)
  const highlightedVisibleMeshes = shallowRef<Set<THREE.Mesh>>(new Set())
  let highlightedHintByMesh = new Map<THREE.Mesh, MeshHint>()

  function tintFor(hint: MeshHint | null, colorMap?: Map<string, number>): number | null {
    if (!hint) return null
    const index = colorMap?.get(hint.articleNumber) ?? articleColorIndexFor(hint.articleNumber)
    return ARTICLE_COLOR_PALETTE[index % ARTICLE_COLOR_PALETTE.length]
  }

  function articlesPresentInGlb(candidates: Set<string>): Set<string> {
    const present = new Set<string>()
    for (const m of allMeshes) {
      for (const n of meshAncestorNames.get(m) ?? []) {
        if (candidates.has(n)) present.add(n)
      }
    }
    return present
  }

  function applyVisibility(
    visibleHints: MeshHint[] | null,
    highlightHints: MeshHint[],
    opts?: ApplyOpts,
  ) {
    const hasFilter = visibleHints !== null
    const visibleFilterHints = visibleHints ?? []
    const hasHighlights = highlightHints.length > 0
    const allHints = [...visibleFilterHints, ...highlightHints]

    const hiddenArticles = opts?.hiddenArticles ?? new Set<string>()
    const candidates = new Set<string>([
      ...(opts?.knownArticleNames ?? []),
      ...allHints.flatMap((h) => h.glbArticleNames ?? [h.articleNumber]),
      ...hiddenArticles,
    ])
    const knownArticleNames = articlesPresentInGlb(candidates)
    const glbMapping = buildGlbNameMapping(
      allMeshes,
      meshAncestorNames,
      allHints,
      knownArticleNames,
    )

    const highlightArticles = new Set(
      highlightHints.flatMap((h) => h.glbArticleNames ?? [h.articleNumber]),
    )

    function instanceKey(m: THREE.Mesh, ancestors: string[]): string {
      const matches = (n: string) =>
        nameMatchesArticle(n, highlightArticles, false, knownArticleNames)
      let idx = ancestors.findIndex(matches)
      if (idx < 0) return 'parent:' + (m.parent?.uuid ?? m.uuid)
      // Leaf mesh shares the article name with its parent → use the parent (the
      // actual assembly node).
      if (idx === 0 && ancestors[1] && matches(ancestors[1])) idx = 1

      let cur: THREE.Object3D | null = m
      let seen = 0
      while (cur) {
        if (cur.name) {
          if (seen === idx) return cur.uuid
          seen++
        }
        cur = cur.parent
      }
      return ancestors.slice(idx).join('\0')
    }

    const instanceMeshesByHint = new Map<MeshHint, Set<THREE.Mesh>>()
    const allowsMesh = (hint: MeshHint, m: THREE.Mesh) => {
      if (!hint.instanceIndexes || !opts?.meshesOfInstances) return true
      let meshes = instanceMeshesByHint.get(hint)
      if (!meshes) {
        meshes = opts.meshesOfInstances(hint, hint.instanceIndexes)
        instanceMeshesByHint.set(hint, meshes)
      }
      return meshes.has(m)
    }
    const restrictsInstances =
      !!opts?.meshesOfInstances && allHints.some((h) => !!h.instanceIndexes)
    const allowedFor = (hints: MeshHint[], m: THREE.Mesh) =>
      restrictsInstances ? hints.filter((h) => allowsMesh(h, m)) : hints

    const hintByMesh = new Map<THREE.Mesh, MeshHint>()
    const visibleByMesh = new Map<THREE.Mesh, boolean>()
    const instancesByHint = new Map<MeshHint, Set<string>>()

    const claim = (m: THREE.Mesh, hint: MeshHint, key: string) => {
      hintByMesh.set(m, hint)
      const s = instancesByHint.get(hint) ?? new Set<string>()
      s.add(key)
      instancesByHint.set(hint, s)
    }

    const ancestorNames = distinctAncestorNames(allMeshes, meshAncestorNames)
    const visibleIndex = new HintIndex(visibleFilterHints, ancestorNames, knownArticleNames)
    const highlightIndex = new HintIndex(highlightHints, ancestorNames, knownArticleNames)

    // Pass 1: strict match by article + requiredAncestors path.
    for (const m of allMeshes) {
      const ancestors = meshAncestorNames.get(m)
      visibleByMesh.set(
        m,
        !hasFilter ||
          (!!ancestors &&
            matchesHintsWithMapping(
              ancestors,
              allowedFor(visibleIndex.candidatesFor(ancestors), m),
              glbMapping,
              knownArticleNames,
            )),
      )
      if (!ancestors || !hasHighlights) continue
      const hint = highlightIndex
        .candidatesFor(ancestors)
        .find(
          (h) =>
            allowsMesh(h, m) &&
            matchesHintsWithMapping(ancestors, [h], glbMapping, knownArticleNames),
        )
      if (hint) claim(m, hint, instanceKey(m, ancestors))
    }

    const highlightMeshesPerHint = highlightIndex.meshesPerHint(allMeshes, meshAncestorNames)
    for (const [hintIndex, hint] of highlightHints.entries()) {
      if (instancesByHint.has(hint)) continue
      for (const m of highlightMeshesPerHint[hintIndex]) {
        if (hintByMesh.has(m) || !allowsMesh(hint, m)) continue
        claim(m, hint, instanceKey(m, meshAncestorNames.get(m)!))
      }
    }

    // Hiding an assembly hides everything inside it, so any hidden ancestor hides the mesh.
    const hiddenByName = new Map<string, boolean>()
    const isHidden = (m: THREE.Mesh) =>
      hiddenArticles.size > 0 &&
      (meshAncestorNames.get(m) ?? []).some((n) => {
        let hidden = hiddenByName.get(n)
        if (hidden === undefined) {
          hidden = nameMatchesArticle(n, hiddenArticles, false, knownArticleNames)
          hiddenByName.set(n, hidden)
        }
        return hidden
      })

    const highlightedVisible = new Set<THREE.Mesh>()
    for (const m of allMeshes) {
      const hint = hintByMesh.get(m) ?? null
      const highlighted = hint !== null
      const visible = !isHidden(m) && (highlighted || (visibleByMesh.get(m) ?? !hasFilter))
      m.visible = visible
      if (highlighted && visible) highlightedVisible.add(m)
      const dim = !hasHighlights || !highlighted
      setMeshAppearance(m, {
        opacity: dim ? DIM_OPACITY : 1.0,
        tint: tintFor(hint, opts?.colorMap),
      })
    }
    highlightedHintByMesh = hintByMesh

    let total = 0
    for (const [hint, s] of instancesByHint) if (!hint.mismatchExempt) total += s.size
    highlightedPartCount.value = total
    highlightedVisibleMeshes.value = highlightedVisible

    if (opts?.skipRecenter) return
    if (opts?.savedViewport) {
      opts.applyViewport?.(opts.savedViewport)
      return
    }
    const box = new THREE.Box3()
    const focus =
      highlightedVisible.size > 0 ? highlightedVisible : allMeshes.filter((m) => m.visible)
    for (const m of focus) box.expandByObject(m)
    if (!box.isEmpty()) opts?.refitToBox?.(box)
  }

  function recolorHighlights(colorMap?: Map<string, number>) {
    for (const [m, hint] of highlightedHintByMesh)
      setMeshAppearance(m, { tint: tintFor(hint, colorMap) })
  }

  function computeHighlightMismatch(highlightHints: MeshHint[]) {
    const allocated = highlightHints.reduce(
      (sum, h) =>
        h.consumable || h.mismatchExempt
          ? sum
          : sum + Math.max(1, Math.round((h.milliQuantity ?? 1000) / 1000)),
      0,
    )
    const highlighted = highlightedPartCount.value
    return allocated > 0 && highlighted !== allocated ? { highlighted, allocated } : null
  }

  function buildHintResolver<T extends SelectableHint>(
    candidateHints: T[],
  ): (ancestors: string[]) => T | null {
    if (candidateHints.length === 0) return () => null
    const articles = new Set(candidateHints.flatMap((h) => h.glbArticleNames ?? [h.articleNumber]))
    const knownArticleNames = articlesPresentInGlb(articles)
    const glbMapping = buildGlbNameMapping(
      allMeshes,
      meshAncestorNames,
      candidateHints,
      knownArticleNames,
    )
    const index = new HintIndex(
      candidateHints,
      distinctAncestorNames(allMeshes, meshAncestorNames),
      knownArticleNames,
    )
    return (ancestors) =>
      bestMatchingHint(ancestors, index.candidatesFor(ancestors), glbMapping, knownArticleNames)
  }

  return {
    highlightedPartCount,
    highlightedVisibleMeshes,
    applyVisibility,
    recolorHighlights,
    computeHighlightMismatch,
    buildHintResolver,
  }
}
